import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import calendars from 'scoreboard/components/calendars'
import config from 'scoreboard/components/config'
import enums from 'scoreboard/components/enums'
import * as scorecardReportColumns from './scorecard-report-columns'
import thresholdTemplate from './thresholds.hbs'
import reportNoteFormatter from 'scoreboard/reports/common/designer-note-formatter'
import escapePreserveLineBreaks from 'scoreboard/components/escape-preserve-line-breaks'
import ReportDataExtractor, {
	formattedRelatedItemHtml,
	getColorLabel
} from 'scoreboard/reports/common/report-data-extractor'
import groupType from 'scoreboard/admin/principals/groups/group-type'
import principalItemsTemplate from 'scoreboard/reports/common/principal-items.hbs'
import principalItemsExportTemplate from 'scoreboard/reports/common/principal-items-export.hbs'
import tagTemplate from '../common/tags.hbs'
import formatValue from 'scoreboard/components/format-value'
import highlightedNameValueListTemplate from 'scoreboard/reports/common/highlighted-name-value-list.hbs'
import highlightedNameValueListExportTemplate from 'scoreboard/reports/common/highlighted-name-value-list-export.hbs'
import currency from 'scoreboard/components/currency'
import unwrapModel from 'scoreboard/components/unwrap-model'
import DataType from 'scoreboard/datasets/model/data-type'
const { YES_NO, THRESHOLD_ID } = DataType
import {
	formattedOverallColorHtml
} from '../common/report-data-extractor'
import { formatItem } from '../../related-items/related-items-list'
import { applyColorStyles } from 'scoreboard/components/colored-cell-formatter'

function principalList(principals) {
	groupType.groupTypeLabelAssignTo(principals)
	let value = principals
	return {
		html: principalItemsTemplate({value}),
		// #44373 HBS is touchy with \n, so just trim it up here
		exportValue: _.trim(principalItemsExportTemplate({value}))
	}
}

function aggregationType(val) {
	return i18n.get(enums.aggregationType[val])
}

const formatYesNo = value => formatValue({
	value,
	dataType: YES_NO
})

const mvtMsg = mvtType =>
	i18n.get(enums.missingValueTreatment[mvtType.missingValueTreatment])

function nameValueList(values, transform) {
	let value = (values || []).map(value => transform(value))
	return {
		html: highlightedNameValueListTemplate({value}),
		exportValue: highlightedNameValueListExportTemplate({value})
	}
}

const htmlExportValue = value => ({
	html: value,
	exportValue: value,
	value
})

const toUnscoredValueByColumnType = {
	'color': function(val) {
		return htmlExportValue(val ? formattedOverallColorHtml(val) : '')
	},
	'calendar-id': function(val) {
		return htmlExportValue(val ? calendars.calendarsById[val].label : '')
	},
	'scoring-type-id': function(val) {
		return htmlExportValue(val ? i18n.get(config.scoringType(val).label) : '')
	},
	'item-type': function(val) {
		return htmlExportValue(i18n.get(enums.scorecardItemType[val]))
	},
	'data-type': function(val) {
		val = val === THRESHOLD_ID ? 'text' : val
		return htmlExportValue(i18n.get(enums.dataType[val]))
	},
	'update-type': function(val) {
		return htmlExportValue(i18n.get(enums.updateType[val]))
	},
	'aggregation-type': value => htmlExportValue(aggregationType(value)),
	'rollup-aggregation-type': value => htmlExportValue(aggregationType(value)),
	'missing-value-treatments': val => {
		return nameValueList(val, (it) => ({
			'name': it.label,
			'value': mvtMsg(it)
		}))
	},
	'owners': val => principalList(val),
	'updaters': val => principalList(val),
	'equations':  val => {
		return nameValueList(val, (it) => ({
			'name': i18n.get(it.name),
			'value': it.equation
		}))
	},
	'tags': val => {
		return {
			html: tagTemplate({values: _.map(val, escapePreserveLineBreaks)}),
			exportValue: _.reduce(val, (acc, v) => {
				acc += `${v}\n`
				return acc
			}, '')
		}
	},
	'higher-better': formatYesNo,
	'yes-good': formatYesNo
}

function getUnscoredValue(value, columnType) {
	let extractor = toUnscoredValueByColumnType[columnType] || _.identity
	return extractor(value)
}

function getScoredValue(score, columnType) {
	switch (columnType) {
		case 'color' : return getColorLabel(score.color)
		default:
			const resultProperty =
				scorecardReportColumns.valueToPropertyName[columnType] ||
					_.camelCase(columnType)
			return score[resultProperty]
	}
}

class ScorecardReportDataExtractor extends ReportDataExtractor {

	resolveContentAttributes(resultItem, columnModel) {
		let currencyCode = _.get(resultItem.currencyCode, 'value')
		let precision = _.get(resultItem.precision, 'value')

		let attributes = _.defaults({currencyCode, precision}, columnModel.attributes)
		// #40923 Goal and actual data types depend on the scorecard item's
		// configuration, and cannot be known in advance; i.e. encoded as
		// constants.
		let { columnType } = attributes
		if (_.includes(['goal', 'actual', 'thresholds', 'variance-goal'], columnType)) {
			attributes.dataType = _.get(resultItem, 'dataType.value',
				attributes.dataType)
		}
		if (columnType == 'score') {
			// #32434 score should always use default decimal precision
			attributes.precision = null
			attributes.isScorecardScore = true
		}
		delete attributes.coercedDataType
		return attributes
	}

	/**
	 * Returns the HTML to be displayed to the end user for a particular
	 * formatted value. Does not include the exportValue and rawValue
	 * @return {String}
	 */
	_formattedValueHtml(value, contentAttributes) {
		let { columnType } = unwrapModel(contentAttributes)
		switch (columnType) {
			case 'color':
				return getColorLabel(value)
			case 'related-items':
				return formattedRelatedItemHtml(value)
			case 'missing-value-treatments':
				return mvtMsg({ missingValueTreatment: value })
			case 'equations': case 'owners': case 'updaters': case 'tags':
				return value
			default:
				let toUnscoredValue = toUnscoredValueByColumnType[columnType]
				if (toUnscoredValue) {
					let unscoredValue = toUnscoredValue(value)
					switch (columnType) {
						case 'higher-better': case 'yes-good':
							return unscoredValue
						default:
							return unscoredValue.html
					}
				} else {
					return value
				}
		}
	}

	extractData(result, resultItem, headerCell) {
		let { contentAttributes } = result
		let columnType = headerCell.columnType
		let { dataType } = contentAttributes
		let property = scorecardReportColumns.valueToPropertyName[columnType] || _.camelCase(columnType)

		// otherwise we're extracting a value for individual rows
		if (columnType == 'id') {
			//special case, for same reason as the aggregation scenario
			return this.applyIdFormatting(result, resultItem)
		}

		let dataValue = _.get(resultItem[property], 'value', null)
		if (columnType == 'notes') {
			result = reportNoteFormatter(result, headerCell.column.get('noteDisplayOptions').displayProperties, dataValue, 'scorecards')
		} else if (columnType == 'related-items') {
			_.each(dataValue, formatItem)
				// the related items are always a list, so that's why we call
				// _formatUniqueListedValues without any regard to the
				// aggregation type
			result.html = this._formatUniqueListedValues(dataValue, contentAttributes)
			result.exportValue = this._valuesToExportString(dataValue, v => `${v.label}`)
		} else if (columnType == 'currency-code') {
			result.html = currency.label(dataValue)
		} else {
			let val = getUnscoredValue(dataValue, columnType)
			if (_.isObject(val)) {
				_.extend(result, val)
			} else {
				if (dataType == 'text') {
					result.html = this.applyTextFormatting(val, columnType, resultItem)
				} else {
					result.html = this.applyValueFormatting(val, contentAttributes)
				}
			}
			result.rawValue = val
		}

		this._storeExportModel(result, dataValue, contentAttributes)
		return result
	}

	/** formatting for the id column */
	applyIdFormatting(result, resultItem) {
		//special case, for same reason as the aggregation scenario
		result.html = `${resultItem.id}` //#44072 format expects a string
		return result
	}

	/** formatting for columns with datatype of text */
	applyTextFormatting(val, columnType, resultItem) {
		return escapePreserveLineBreaks(val)
	}

	toNonRepeatedAggregatedValue(result, resultItem, headerCell) {
		let {column, columnType} = headerCell
		let property = scorecardReportColumns.valueToPropertyName[columnType] || _.camelCase(columnType)
		if (columnType == 'id') {
			//special case, due to the API the id property is not using a
			//ReportResultDatum to be backwards compatiable with the legacy reports
			//we'll get rid of this shim once we remove the legacy reports
			//the server will just store the aggregation in numberOfRecords property since they are functionally equivalent
			property = 'numberOfRecords'
		}
		return _.get(resultItem[property], `aggregationsByColumnClientId.${column.columnClientId()}`, '')
	}

	_setBreakdownDataByStackClientIdStyles(result, valueAndFormatting, columnAttributes) {
		if (columnAttributes.colorType == 'scoring') {
			let scoringColor = _.get(valueAndFormatting, 'scoringColor', null)
			if (scoringColor) {
				applyColorStyles(result, scoringColor)
			}
		}
	}

	nonAggregatedPeriodBasedColumnDataContents(result, resultItem, headerCell) {
		let { breakdownColumnData } = headerCell
		let { breakdownItemId } = breakdownColumnData
		let { contentAttributes } = result
		let { columnType } = contentAttributes

		if (scorecardReportColumns.isColumnScored(columnType) || columnType == 'weight') {
			let { colorType } = contentAttributes
			let periodId = _.parseInt(breakdownItemId.firstBreakdownItemId)
			let scores = _.get(resultItem, 'scores.value', {})
			let score = _.keyBy(scores, 'periodId')[periodId] || {}
			let overrides = _.isNil(score.dataType) ? {} : {dataType: score.dataType}

			if (resultItem.dataType == THRESHOLD_ID) {
				overrides.dataType = resultItem.dataType
			}

			if (columnType == 'thresholds') {
				//special case for thresholds
				let values = []
				let thresholdSeries = _.get(resultItem.thresholds, 'value', [])
				_(thresholdSeries).filter(val => !_.isNil(val)).each((threshold, idx) => {
					values[idx] = {label: threshold.label}
				})
				if (values.length) {
					let scoredValue = getScoredValue(score, columnType)
					_.each(scoredValue, (thresholdValue, idx) => {
						if (!_.isNil(thresholdValue) && values[idx]) {
							values[idx].rawValue = thresholdValue
							values[idx].value = this.applyValueFormatting(thresholdValue,
								contentAttributes, overrides)
						}
					})
					result.html = thresholdTemplate({values})

					//for exports, we'll turn this into a string of raw values
					overrides.exportValue = this._valuesToExportString(values,
						v => {
							let transformedValued =  _.get(v, 'rawValue', '')
							if (resultItem.dataType == THRESHOLD_ID) {
								//for threshold-ids we need to transformed value
								transformedValued = _.get(v, 'value', '')
							}
							return `${v.label}: ${transformedValued}`
						})
					this._storeExportModel(result, null, contentAttributes, overrides)
				}
			} else {
				this.setNonThresholdScoredValueContent(result, score, contentAttributes, overrides)
			}

			//we only apply color if we're using scoring color type
			let showColor = colorType == 'scoring'
			result.style['color'] = result.style['color'] || showColor
			if (showColor) {
				let color = _.get(score, 'color', 'gray')
				applyColorStyles(result, color)
			}
		} else {
			result = this.extractData(result, resultItem, _.defaults({columnType}, headerCell))
		}

		return result
	}

	/**
	 * Sets the cell contents for the value for a non threshold scored column
	 * @param {Object} cell The object containing data for a cell in the report
	 * @param {Object} score The server side ScoreWithWeighting Object
	 * @param {Number} columnType The column type
	 * @param {Object} contentAttributes The attributes on the cell
	 */
	setNonThresholdScoredValueContent(cell, score, contentAttributes, overrides = {}) {
		if (_.isEmpty(score)) { return }

		let { columnType } = contentAttributes
		let scoredValue = getScoredValue(score, columnType)
		if (score.applicable) {
			cell.html = this.applyValueFormatting(scoredValue,
				contentAttributes, overrides)
			this._storeExportModel(cell, scoredValue, contentAttributes,
				overrides)
		} else {
			cell.html = i18n.get('scorecards.node.metric.notapplicable')
		}
	}

	formatForGroupHeader(grouping, columnAttributes) {
		let { displayValue } = grouping
		let columnType = columnAttributes.columnType
		let formattedValue = getUnscoredValue(displayValue, columnType)
		if (columnType == 'missing-value-treatments') {
			return _.join(_.map(displayValue, mvtMsg), ', ')
		} else if (columnType == 'equations') {
			return _.join(_.map(displayValue, v => v.equation.trim()), ', ')
		}

		return typeof formattedValue === 'object' ?
			formattedValue.html : formattedValue
	}

	applyValueFormatting(value, contentAttributes = {}, overrides = {}) {
		let { columnType } = contentAttributes
		if (columnType == 'score') {
			overrides.dataType = 'score'
		} else if (_.includes(['percent-goal','percent-variance-goal'], columnType)) {
			overrides.dataType = 'percent' //#47611 these are always percents
		} else if (columnType == 'color') {
			overrides.dataType = 'text' //#48223 colors are always text
		} else if (columnType == 'actual') {

		}
		return ReportDataExtractor.prototype.applyValueFormatting
			.call(this, value, contentAttributes, overrides)
	}

}

export default ScorecardReportDataExtractor

ScorecardReportDataExtractor.__filename__ = 'reports/scorecard-report/scorecard-report-data-extractor.js'
